import React, { useCallback, useMemo, useState } from 'react';
import { View, Text, Image, StyleSheet, Platform, useWindowDimensions } from 'react-native';
import { AgapellaChatThread } from '../../components/chat/AgapellaChatThread';
import { useChatThreadSource } from '../../hooks/useChatThreadSource';
import { GuestChatSource } from '../../services/chat/guestChatSource';
import { CHAT_API_BASE_URL } from '../../constants/chat';
import { displayFont, uiFont } from '../../styles/agapellaFonts';
import { Button } from '../../components/ui/Button';
import { AGAPELLA_AI_ICON } from '../../constants/aiCompanions';
import { GUEST_GREETING } from './guestGreeting';
import { GuestAgapellaInvite } from './GuestAgapellaInvite';
import { AiNoticeLink } from '../../components/chat/AiNoticeLink';

/**
 * CORE-1448 — разговор с Агапеллой до регистрации, на публичной странице.
 *
 * ## Почему это отдельная секция, а не «чат как у всех»
 *
 * Гость не ходит через SignalR-хаб: `[Authorize]` там стоит на классе целиком,
 * и тем же хабом ходят личные сообщения, круги и блокировки. Весь гостевой
 * гейт — кука, счётчик ходов, сервисный потолок — живёт в чат-сервисе, и гость
 * обращается туда напрямую (`GuestChatSource`). На бэкенде ради этого не
 * снимается ни одна авторизация.
 *
 * ## Что здесь намеренно НЕ показано
 *
 * Счётчик оставшихся ходов. Сервер знает остаток, но в потоке ответа его нет, и
 * рисовать «осталось 3» по догадке клиента значило бы врать в мелочи, которую
 * человек проверит. Шестой ход и так приходит приглашением зарегистрироваться —
 * его пишет сервер, в голосе Агапеллы.
 *
 * Вложения и голос: вложение стоит дороже всех пяти текстовых ходов гостя, а
 * микрофон требует своего разговора про расшифровку.
 *
 * ## Уведомление об ИИ (CORE-1942)
 *
 * Над полем ввода — та же тихая строка, что в чате вошедшего человека
 * (`AiNoticeLink`): «Ответы формирует ИИ · Уведомление об ИИ». Гость видит её до
 * первого сообщения. Это пометка и ссылка, а не согласие: ни галочки, ни
 * блокировки ввода.
 *
 * ## Кому это видно
 *
 * Только гостю и только в вебе: источник построен на потоковом `fetch`, у
 * нативного композера своя дорога. Вошедший человек видит полноценный чат в
 * «Чатах», и подсовывать ему усечённый здесь незачем.
 */

const WIDGET_LOTUS = require('../../../assets/home/agapella-approved-lotus.webp');
const PEER = { name: 'Агапелла', avatarUrl: null, brandedAvatar: AGAPELLA_AI_ICON };

interface Props {
  isDark: boolean;
  openRequest?: number;
  /** Открыть регистрацию — кнопка под разговором. */
  onRegister: () => void;
  onLogin: () => void;
}

export const GuestAgapellaSection: React.FC<Props> = ({ isDark, onRegister, onLogin, openRequest = 0 }) => {
  const [open, setOpen] = useState(false);
  const { width } = useWindowDimensions();
  const mobile = width < 768;
  const threadRef = React.useRef<View>(null);
  React.useEffect(() => { if (openRequest > 0) setOpen(true); }, [openRequest]);
  React.useEffect(() => {
    if (!open || openRequest === 0 || Platform.OS !== 'web') return;
    (threadRef.current as unknown as { scrollIntoView?: (options: { block: string }) => void })?.scrollIntoView?.({ block: 'center' });
  }, [open, openRequest]);

  // Источник создаётся только после того, как человек сам открыл разговор:
  // иначе приветствие и подписка жили бы у каждого, кто просто пролистал
  // главную.
  const makeSource = useMemo(
    () => (open
      ? async () => new GuestChatSource({
        baseUrl: CHAT_API_BASE_URL,
        greeting: GUEST_GREETING,
      })
      : null),
    [open],
  );

  const { messages, thinking, loading, error, send } = useChatThreadSource(makeSource);

  const onSend = useCallback((text: string) => { void send(text); }, [send]);

  if (Platform.OS !== 'web') return null;

  if (!open) {
    return <GuestAgapellaInvite isDark={isDark} onOpen={() => setOpen(true)} />;
  }

  const s = styles(isDark);

  return (
    <View ref={threadRef} style={[s.card, mobile && { padding: 16 }]} testID="guest-agapella-thread">
      <View style={s.header}>
        <Image source={WIDGET_LOTUS} style={s.logo} resizeMode="contain" accessible={false} />
        <View style={s.headingCopy}>
          <Text accessibilityRole="header" style={[s.title, mobile && { fontSize: 23, lineHeight: 30 }]}>Спроси Агапеллу</Text>
          <Text style={s.subtitle}>Помогу сориентироваться и найти то, что поддержит тебя сейчас.</Text>
        </View>
      </View>
      <View style={[s.threadFrame, mobile && { height: 440 }]}>
        <AgapellaChatThread
          appearance="home"
          messages={messages}
          thinking={thinking}
          loading={loading}
          error={error}
          peer={PEER}
          onSend={onSend}
          placeholder="Напиши, что хочется узнать…"
          composerTopSlot={<AiNoticeLink />}
        />
      </View>
      <View style={s.footer}>
        <Button variant="outline" accessibilityRole="button" onPress={onRegister} testID="guest-agapella-register" style={[s.cta, s.register, mobile && { flexGrow: 1 }]} textStyle={s.ctaText}>Создать аккаунт</Button>
        <Button variant="outline" accessibilityRole="button" onPress={onLogin} testID="guest-agapella-login" style={s.cta} textStyle={s.loginText}>Войти</Button>
      </View>
    </View>
  );
};

const styles = (isDark: boolean) => StyleSheet.create({
  card: { borderRadius: 24, padding: 28, gap: 20, backgroundColor: isDark ? '#292D30' : '#FFFEF7', borderWidth: 1, borderColor: isDark ? '#545658' : '#D8CCBA' },
  header: { flexDirection: 'row', alignItems: 'center', gap: 16 },
  logo: { width: 44, height: 44 },
  headingCopy: { flex: 1, gap: 6 },
  title: { ...displayFont(), fontSize: 28, lineHeight: 36, color: isDark ? '#F0ECE4' : '#314C48' },
  subtitle: { ...uiFont(), fontSize: 14, lineHeight: 21, color: isDark ? '#BCB9B4' : '#62675E' },
  threadFrame: { height: 420, overflow: 'hidden', borderRadius: 20, backgroundColor: isDark ? '#24282B' : '#F5F3EA', borderWidth: 1, borderColor: isDark ? '#545658' : '#D8CCBA' },
  footer: { flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'flex-end', gap: 12 },
  cta: { minHeight: 48, borderRadius: 16, paddingHorizontal: 20, borderColor: isDark ? '#A99C86' : '#A6AA94', borderWidth: 1.5, backgroundColor: 'transparent' },
  register: { backgroundColor: isDark ? '#E8CCAA' : '#F5E5CC', borderColor: isDark ? '#CFB28D' : '#D6BD97' },
  ctaText: { ...uiFont(700), fontSize: 15, lineHeight: 22, color: '#3D352B' },
  loginText: { ...uiFont(700), fontSize: 15, lineHeight: 22, color: isDark ? '#F0ECE4' : '#314C48' },
});
